import React, { useState, useEffect } from 'react';
import {
    AcademicCapIcon,
    ClipboardDocumentCheckIcon,
    ChevronRightIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import SearchableDropdown from '../common/SearchableDropdown';

const BlueprintCreator = ({
    learningAreas = [],
    onSuccess,
    onCancel,
    initialType = 'assessment',
    assignmentContext = null,
    initialData = null
}) => {
    const { get, post, patch } = useApi();
    const { showToast } = useAppState();
    const [submitting, setSubmitting] = useState(false);
    const [tab] = useState(initialData ? 'assessment' : initialType);
    const [gradeLevels, setGradeLevels] = useState([]);
    const [strands, setStrands] = useState([]);
    const [loadingStrands, setLoadingStrands] = useState(false);

    const resolveGradeId = (ctx, data) => {
        if (data?.grade_level) return typeof data.grade_level === 'object' ? data.grade_level.id : parseInt(data.grade_level);
        if (data?.grade_level_id) return parseInt(data.grade_level_id);
        if (ctx?.grade_level) return typeof ctx.grade_level === 'object' ? ctx.grade_level.id : parseInt(ctx.grade_level);
        if (ctx?.grade_level_id) return parseInt(ctx.grade_level_id);
        return null;
    };

    const [newAssessment, setNewAssessment] = useState({
        name: initialData?.name || '',
        learning_area: initialData?.learning_area || assignmentContext?.learning_area || assignmentContext?.course || null,
        strand: initialData?.strand || null,
        grade_level: resolveGradeId(assignmentContext, initialData),
        club: initialData?.club || assignmentContext?.club || null,
        description: initialData?.description || '',
        criteria_ee: initialData?.criteria_ee || '',
        criteria_me: initialData?.criteria_me || '',
        criteria_ae: initialData?.criteria_ae || '',
        criteria_be: initialData?.criteria_be || ''
    });

    const isEdit = !!initialData;
    const [fetchedContext, setFetchedContext] = useState(null);

    useEffect(() => {
        const areaId = assignmentContext?.learning_area || initialData?.learning_area;
        if (areaId) {
            get(`/teachers/api/courses/${areaId}/`).then(data => {
                if (data) {
                    const resolvedGradeId = data.grade_level_id || (typeof data.grade_level === 'object' ? data.grade_level?.id : data.grade_level);
                    setFetchedContext({
                        learning_area_name: data.name || data.learning_area_name || '',
                        grade_level_name: data.grade_level_name || data.grade_level?.name || '',
                        grade_level_id: resolvedGradeId
                    });
                    setNewAssessment(prev => ({
                        ...prev,
                        grade_level: prev.grade_level || resolvedGradeId || null
                    }));
                }
            }).catch(err => console.error('Error fetching course context:', err));
        }
    }, [assignmentContext, initialData, get]);

    useEffect(() => {
        const fetchGrades = async () => {
            try {
                const data = await get('/api/cbc/grade-levels/');
                setGradeLevels(data || []);
            } catch (err) {
                console.error('Error fetching grade levels:', err);
            }
        };
        fetchGrades();
    }, [get]);

    // Fetch strands when learning area changes
    useEffect(() => {
        const fetchStrands = async () => {
            if (!newAssessment.learning_area || tab !== 'assessment') {
                setStrands([]);
                return;
            }
            try {
                setLoadingStrands(true);
                const data = await get(`/api/cbc/strands/?learning_area=${newAssessment.learning_area}`);
                setStrands(data || []);
            } catch (err) {
                console.error('Error fetching strands:', err);
                showToast('Failed to load strands', 'error');
            } finally {
                setLoadingStrands(false);
            }
        };
        fetchStrands();
    }, [newAssessment.learning_area, tab, get, showToast]);

    const handleCreateAssessment = async () => {
        if (!newAssessment.name || (!newAssessment.learning_area && !newAssessment.club)) {
            showToast('Please fill in required fields (Name, Learning Area)', 'error');
            return;
        }

        try {
            setSubmitting(true);
            let data;
            
            // Ensure IDs are processed correctly
            const payload = {
                ...newAssessment,
                learning_area: newAssessment.learning_area ? parseInt(newAssessment.learning_area) : null,
                strand: newAssessment.strand ? parseInt(newAssessment.strand) : null,
                grade_level: newAssessment.grade_level ? parseInt(newAssessment.grade_level) : null,
                club: newAssessment.club ? parseInt(newAssessment.club) : null
            };

            if (isEdit) {
                data = await patch(`/api/cbc/templates/${initialData.id}/`, payload);
                showToast('Rubric updated successfully! ✨');
            } else {
                data = await post('/api/cbc/templates/', payload);
                showToast('Rubric forged successfully! ✨');
            }
            onSuccess(data);
        } catch (error) {
            console.error('Error saving assessment template:', error);
            showToast('Failed to save assessment rubric', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col animate-in zoom-in duration-300">
                <div className="w-full animate-in fade-in zoom-in-95 duration-500 overflow-hidden flex flex-col flex-1 min-h-0">
            {/* Redesigned Header - Matching AssignmentCreator */}
            <div className="px-10 py-8 border-b border-slate-100 bg-white flex items-center justify-between z-10 shrink-0">
                <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-[#18216D] border border-indigo-100 shadow-sm">
                        <ClipboardDocumentCheckIcon className="w-7 h-7 stroke-[2.5]" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tighter italic leading-none">
                            {isEdit ? 'Edit' : 'Create'} <span className="text-blue-600">Assessment Rubrics</span>
                        </h2>
                        <div className="flex items-center gap-3 mt-2">
                            <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[9px] font-black uppercase tracking-[0.2em] rounded-xl border border-amber-100">
                                CBC Standards
                            </span>
                        </div>
                    </div>
                </div>

                {onCancel && (
                    <button
                        onClick={onCancel}
                        className="p-3 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-2xl transition-all border border-transparent hover:border-rose-100"
                        title="Close"
                    >
                        <XMarkIcon className="w-6 h-6 stroke-[3]" />
                    </button>
                )}
            </div>



            <div className="p-10 bg-slate-50 space-y-12 flex-1 overflow-y-auto custom-scrollbar">
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="max-w-5xl mx-auto space-y-12">
                        {/* Identity Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 ml-1">
                                    <div className="w-1.5 h-3.5 bg-blue-600 rounded-full" />
                                    <label className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-[0.2em]">Title</label>
                                </div>
                                <input
                                    type="text"
                                    value={newAssessment.name}
                                    onChange={(e) => setNewAssessment({ ...newAssessment, name: e.target.value })}
                                    className="w-full px-5 py-3 bg-white border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] focus:border-[#18216D] focus:ring-4 focus:ring-indigo-50/20 transition-all outline-none shadow-sm placeholder:text-slate-400 text-sm"
                                    placeholder="e.g. Grade 4 Mathematics - Geometry"
                                />
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 ml-1">
                                    <div className="w-1.5 h-3.5 bg-[#18216D] rounded-full" />
                                    <label className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-[0.2em]">Grade Level</label>
                                </div>
                                {assignmentContext ? (
                                    <div className="w-full px-5 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] shadow-sm flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-3 text-slate-600">
                                            <AcademicCapIcon className="w-5 h-5 text-[#18216D]" />
                                            <span>{initialData?.grade_level_name || assignmentContext.grade_level_name || fetchedContext?.grade_level_name || assignmentContext.level_name || 'Grade Level'}</span>
                                        </div>
                                        <span className="text-[9px] bg-slate-200 text-slate-500 px-2.5 py-0.5 rounded-full uppercase italic font-bold">LOCKED</span>
                                    </div>
                                ) : (
                                    <select
                                        value={newAssessment.grade_level}
                                        onChange={(e) => setNewAssessment({ ...newAssessment, grade_level: e.target.value })}
                                        className="w-full px-5 py-3 bg-white border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] focus:border-[#18216D] focus:ring-4 focus:ring-indigo-50/20 transition-all outline-none shadow-sm text-sm cursor-pointer"
                                    >
                                        <option value="">Select Grade</option>
                                        {gradeLevels.map(grade => (
                                            <option key={grade.id} value={grade.id}>{grade.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                            {/* Categorization Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 ml-1">
                                    <div className="w-1.5 h-3.5 bg-[#18216D] rounded-full" />
                                    <label className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-[0.2em]">Learning Areas</label>
                                </div>
                                {assignmentContext ? (
                                    <div className="w-full px-5 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] shadow-sm flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-3 text-slate-600">
                                            <div className="w-2.5 h-2.5 rounded-full bg-blue-600 shadow-md shadow-blue-400/40" />
                                            <span>{initialData?.learning_area_name || assignmentContext.learning_area_name || fetchedContext?.learning_area_name || assignmentContext.course_name || assignmentContext.strand_name || 'Learning Area'}</span>
                                        </div>
                                        <span className="text-[9px] bg-slate-200 text-slate-500 px-2.5 py-0.5 rounded-full uppercase italic font-bold">LOCKED</span>
                                    </div>
                                ) : (
                                    <select
                                        value={newAssessment.learning_area}
                                        onChange={(e) => {
                                            const areaId = e.target.value ? parseInt(e.target.value) : null;
                                            const area = learningAreas.find(a => a.id === areaId);
                                            setNewAssessment({
                                                ...newAssessment,
                                                learning_area: areaId,
                                                grade_level: area?.grade_level || newAssessment.grade_level,
                                                strand: null // Reset strand on area change
                                            });
                                        }}
                                        className="w-full px-5 py-3 bg-white border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] focus:border-[#18216D] focus:ring-4 focus:ring-indigo-50/20 transition-all outline-none shadow-sm text-sm cursor-pointer"
                                    >
                                        <option value="">Select Learning Area</option>
                                        {newAssessment.learning_area && !learningAreas.find(a => a.id === parseInt(newAssessment.learning_area)) && (
                                            <option key="fallback" value={newAssessment.learning_area}>
                                                Master/Shared Subject (Retain Link)
                                            </option>
                                        )}
                                        {learningAreas.filter(a => !a.stream).map(area => (
                                            <option key={area.id} value={area.id}>{area.name} - {area.grade_level_name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2 ml-1">
                                    <div className="w-1.5 h-3.5 bg-blue-600 rounded-full" />
                                    <label className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-[0.2em]">Strand</label>
                                </div>
                                <div className="relative">
                                    <SearchableDropdown
                                        options={strands}
                                        selectedValues={newAssessment.strand ? [newAssessment.strand] : []}
                                        onChange={(id) => {
                                            const strandId = id ? parseInt(id) : null;
                                            const strandObj = strands.find(s => s.id === strandId);
                                            setNewAssessment({ 
                                                ...newAssessment, 
                                                strand: strandId,
                                                name: newAssessment.name || (strandObj ? `${newAssessment.learning_area_name || ''} - ${strandObj.name}` : '')
                                            });
                                        }}
                                        placeholder={loadingStrands ? 'Loading Strands...' : 'Select Strand'}
                                        disabled={!newAssessment.learning_area || loadingStrands}
                                    />
                                    {!newAssessment.learning_area && (
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase italic">Choose Area First</div>
                                    )}
                                </div>
                            </div>
                            </div>

                        <div className="space-y-2 mt-4">
                            <div className="flex items-center gap-2 ml-1">
                                <div className="w-1.5 h-3.5 bg-[#18216D] rounded-full" />
                                <label className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-[0.2em]">Strategic Intent</label>
                            </div>
                            <textarea
                                value={newAssessment.description}
                                onChange={(e) => setNewAssessment({ ...newAssessment, description: e.target.value })}
                                rows="3"
                                className="w-full px-5 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-[#18216D] focus:border-[#18216D] focus:ring-4 focus:ring-indigo-50/20 transition-all outline-none shadow-sm placeholder:text-slate-400 text-sm leading-relaxed"
                                placeholder="Briefly describe what this rubric measures..."
                            />
                        </div>

                            {/* Rubric Criteria Cards */}
                            <div className="space-y-6 pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="h-[1px] flex-1 bg-slate-200" />
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] italic">Assessment Levels</span>
                                    <div className="h-[1px] flex-1 bg-slate-200" />
                                </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {[
                                    { key: 'criteria_ee', label: 'EE - Exceeding', color: 'border-amber-400', bg: 'bg-amber-50/40' },
                                    { key: 'criteria_me', label: 'ME - Meeting', color: 'border-emerald-400', bg: 'bg-emerald-50/40' },
                                    { key: 'criteria_ae', label: 'AE - Approaching', color: 'border-blue-400', bg: 'bg-blue-50/40' },
                                    { key: 'criteria_be', label: 'BE - Below', color: 'border-rose-400', bg: 'bg-rose-50/40' }
                                ].map(level => (
                                    <div key={level.key} className="group p-6 bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:border-[#18216D] focus-within:border-[#18216D] focus-within:ring-4 focus-within:ring-indigo-50/20 transition-all duration-300">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2.5">
                                                <div className={`w-3 h-3 rounded-full ${level.color.replace('border-', 'bg-')} shadow-sm`} />
                                                <label className="text-[11px] font-black text-[#18216D] uppercase tracking-widest">{level.label}</label>
                                            </div>
                                            <div className={`px-2.5 py-0.5 ${level.bg} ${level.color.replace('border-', 'text-')} rounded-full text-[8px] font-black uppercase tracking-widest`}>
                                                Tier Achievement
                                            </div>
                                        </div>
                                        <textarea
                                            value={newAssessment[level.key]}
                                            onChange={(e) => setNewAssessment({ ...newAssessment, [level.key]: e.target.value })}
                                            rows="4"
                                            className="w-full bg-transparent border-none rounded-xl font-medium text-[#18216D] transition-all outline-none placeholder:text-slate-400 text-xs leading-relaxed"
                                            placeholder={`Define indicators for ${level.label}...`}
                                        />
                                    </div>
                                ))}
                            </div>
                            </div>

                            <div className="pt-10 pb-6 flex items-center justify-between">
                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic flex items-center gap-2">
                                    <ClipboardDocumentCheckIcon className="w-4 h-4 text-[#18216D]" />
                                    Kianda Blueprint Registry
                                </div>
                                <button
                                    onClick={handleCreateAssessment}
                                    disabled={submitting}
                                    className="px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#FFC425] hover:text-[#18216D] shadow-xl transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3 group"
                                >
                                    {submitting ? 'Saving...' : 'Save Rubric'}
                                    <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
};

export default BlueprintCreator;
